// FooterSiteMap — the site map both footers render (SiteFooter on reference
// pages, DesertFooter's full form on inner Desert Book pages), from the one
// list in src/data/site-map.ts. Desktop: one column per group. Phone (≤720px,
// tokens.css): native <details> rows that open by tap or keyboard.

import { Link } from "wouter";
import { LinkMark } from "./icons";
import { SITE_MAP, type SiteMapLink } from "../data/site-map";

export type FooterPalette = { head: string; text: string; dim: string; line: string };

export default function FooterSiteMap({ palette }: { palette: FooterPalette }) {
  const heading: React.CSSProperties = {
    fontFamily: "var(--mono)",
    fontSize: 10,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: palette.head,
    margin: "0 0 14px 0",
    fontWeight: 500,
  };
  const list = (links: SiteMapLink[], mobile = false) => (
    <ul style={{ listStyle: "none", padding: mobile ? "0 0 16px" : 0, margin: 0, display: "flex", flexDirection: "column", gap: mobile ? 10 : 8 }}>
      {links.map((l) => (
        <Item key={l.href} link={l} palette={palette} />
      ))}
    </ul>
  );
  return (
    <nav
      aria-label="Site map"
      className="site-footer-map"
      style={{ display: "grid", gridTemplateColumns: `repeat(${SITE_MAP.length}, minmax(0, 1fr))`, gap: 28 }}
    >
      {SITE_MAP.map((col) => (
        <div className="site-footer-desktop-column" key={col.heading}>
          <h2 style={heading}>{col.heading}</h2>
          {list(col.links)}
        </div>
      ))}
      {SITE_MAP.map((col) => (
        <details className="site-footer-mobile-column" key={`mobile-${col.heading}`} style={{ borderBottom: `1px solid ${palette.line}` }}>
          <summary style={{ cursor: "pointer", minHeight: 44, padding: "12px 0", fontFamily: "var(--mono)", fontSize: 12, color: palette.head }}>
            {col.heading} <span style={{ color: palette.dim }}>· {col.links.length}</span>
          </summary>
          {list(col.links, true)}
        </details>
      ))}
    </nav>
  );
}

/** One entry. A group shows only its hub link; the hub lists what is inside. */
function Item({ link, palette }: { link: SiteMapLink; palette: FooterPalette }) {
  const style: React.CSSProperties = {
    fontFamily: "var(--sans)",
    fontSize: 13,
    color: palette.text,
    textDecoration: "none",
    letterSpacing: 0.1,
    lineHeight: 1.4,
    whiteSpace: "pre-line",
    transition: "color 150ms var(--ease-out-soft)",
  };
  return (
    <li>
      {link.external ? (
        <a href={link.href} target="_blank" rel="noreferrer" style={style}>
          {link.label} <LinkMark />
        </a>
      ) : (
        <Link href={link.href} style={style}>
          {link.label}
        </Link>
      )}
    </li>
  );
}
